import { useEffect, useState, useCallback } from "react";
import { Trash2, Search, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge } from "../../components/dashboard/Shared";

const ROLES = ["investor", "manager", "super_admin"];
const STATUSES = ["active", "suspended"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
  catch { return iso.slice(0, 10); }
};

export default function Users() {
  const [users, setUsers] = useState(null);
  const [subs, setSubs] = useState({});   // user_id -> row from /admin/subscriptions
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");

  const load = useCallback(() => {
    Promise.all([
      api.get(`/admin/users?search=${encodeURIComponent(search)}&role=${role}`),
      api.get(`/admin/subscriptions?limit=1000`),
    ])
      .then(([u, s]) => {
        setUsers(u.data);
        const map = {};
        (s.data.rows || []).forEach((r) => { map[r.user_id] = r; });
        setSubs(map);
      })
      .catch(() => setUsers([]));
  }, [search, role]);
  useEffect(() => { load(); }, [load]);

  const update = async (u, patch) => {
    try {
      await api.put(`/admin/users/${u.user_id}`, patch);
      toast.success("User updated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <PageHeader title="User Management" sub="Roles, status, subscriptions and access control" />
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-3 text-gray-500" />
          <input data-testid="user-search-input" className="input-dark pl-9" placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select data-testid="user-role-filter" className="input-dark !w-44" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      {!users ? <div className="skeleton h-96" /> : (
        <Table headers={["User", "Role", "Plan", "Sub Status", "Expiry", "MT5 Used/Limit", "KYC", "Status", ""]} testId="admin-users-table">
          {users.map((u) => {
            const s = subs[u.user_id] || {};
            const overLimit = s.mt5_limit >= 0 && s.mt5_used >= s.mt5_limit;
            return (
              <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.user_id}`}>
                <Td>
                  <Link to={`/admin/users/${u.user_id}`} className="text-white hover:text-[#00F0FF] flex items-center gap-1.5" data-testid={`user-link-${u.user_id}`}>
                    {u.name} <ExternalLink size={11} className="opacity-50" />
                  </Link>
                  <p className="text-xs text-gray-500">{u.email}</p>
                </Td>
                <Td>
                  <select data-testid={`role-select-${u.user_id}`} className="input-dark !py-1 !px-2 !w-32 text-xs" value={u.role} onChange={(e) => update(u, { role: e.target.value })}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Td>
                <Td className="text-[#00F0FF] text-xs">{s.plan_code || "—"}</Td>
                <Td><StatusBadge status={s.status} /></Td>
                <Td className="text-xs text-gray-400">{fmtDate(s.expiry_date)}</Td>
                <Td className="tabular text-xs">
                  <span className={overLimit ? "text-[#FF3B30]" : "text-white"}>{s.mt5_used ?? 0}</span>
                  <span className="text-gray-500"> / {s.mt5_limit_display || "—"}</span>
                </Td>
                <Td><StatusBadge status={u.kyc_status} /></Td>
                <Td>
                  <select data-testid={`status-select-${u.user_id}`} className="input-dark !py-1 !px-2 !w-24 text-xs" value={u.status} onChange={(e) => update(u, { status: e.target.value })}>
                    {STATUSES.map((s2) => <option key={s2} value={s2}>{s2}</option>)}
                  </select>
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/subscriptions`}
                      className="text-[#7000FF] hover:text-white text-xs flex items-center gap-1"
                      data-testid={`user-upgrade-${u.user_id}`}
                      title="Manage subscription">
                      <Sparkles size={12} /> Upgrade
                    </Link>
                    <button data-testid={`delete-user-${u.user_id}`} onClick={() => remove(u)} className="text-gray-400 hover:text-[#FF3B30]"><Trash2 size={14} /></button>
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}
    </div>
  );
}
